import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, mapTo } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { VuState } from '../models/vu-state';
import { VuPaymentType } from '../../payment/models/vu-payment-type.enum';
import { LoggerService } from '../../../services/logger/logger.service';
import { ComponentRect } from '../models/component-rect';
import { TranslateService } from '@ngx-translate/core';
import { Order } from '../../order/models/order';
import { ScreenSaverConfiguration } from '../models/screen-saver-configuration';
import { VuConfiguration } from '../models/vu-configuration';

@Injectable()
export class VuConnectionHttpService {

  private _VUBaseUrl = '';

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
    private location: Location,
    private translateService: TranslateService,
  ) {
  }

  get vuBaseUrl(): string {
    if (this._VUBaseUrl) {
      return this._VUBaseUrl;
    }
    return environment.vuUrl;
  }

  set vuBaseUrl(vuBaseUrl: string) {
    this._VUBaseUrl = vuBaseUrl;
  }

  pingAndGetVuState(): Observable<VuState> {
    return this.http
      .post(`${this.vuBaseUrl}/api/update/angular/state`, {})
      .pipe(
        map((result: any) => {
          return VuState.createFromAny(result);
        })
      );
  }

  createOrder(order: Order): Observable<Order> {
    return this.http
      .post(`${this.vuBaseUrl}/api/order-cache`, order)
      .pipe(
        map((result: any) => {
          order.id = -1;
          return order;
        })
      );
  }

  getVuConfiguration(): Observable<VuConfiguration> {
    return this.http.get(`${this.vuBaseUrl}/api/get-vu-configuration`)
      .pipe(
        map(result => VuConfiguration.createFromAny(result)),
      );
  }

  getScreenSaverConfiguration(): Promise<ScreenSaverConfiguration> {
    return this.http
      .get(`${this.vuBaseUrl}/api/screensaver/settings`)
      .toPromise()
      .then(response => ScreenSaverConfiguration.fromJson(response))
      .catch(error => {
        this.loggerService.error(error);
        return Promise.reject(error.message || error);
      });
  }

  touchKeyboardShow(touchKeyboardPosition: ComponentRect): Observable<boolean> {
    return this.http
      .post(`${this.vuBaseUrl}/api/touch-keyboard/show`, touchKeyboardPosition)
      .pipe(
        map((result: any) => {
          return true;
        }),
        catchError((error) => {
          this.loggerService.error(error);
          return of(false);
        })
      );
  }

  touchKeyboardHide(): Observable<boolean> {
    return this.http
      .post(`${this.vuBaseUrl}/api/touch-keyboard/hide`, {})
      .pipe(
        map((result: any) => {
          return true;
        }),
        catchError((error) => {
          this.loggerService.error(error);
          return of(false);
        })
      );
  }

  touchTileRfidCardLedEnable(enable: boolean): Observable<boolean> {
    return this.http
      .post(`${this.vuBaseUrl}/api/touch-tile/rfid-card-led-enable`, {
        enabled: enable
      })
      .pipe(
        map((result: any) => {
          return true;
        }),
        catchError((error) => {
          this.loggerService.error(error);
          return of(false);
        })
      );
  }

  touchTileCardDispenserLedEnable(enable: boolean): Observable<boolean> {
    return this.http
      .post(`${this.vuBaseUrl}/api/touch-tile/card-dispenser-led-enable`, {
        enabled: enable
      })
      .pipe(
        map((result: any) => {
          return true;
        }),
        catchError((error) => {
          this.loggerService.error(error);
          return of(false);
        })
      );
  }

  canAcceptCash(amount: number): Observable<boolean> {
    return this.http
      .get(
        `${this.vuBaseUrl}/api/payment/get-accepted-cash/?value=${amount}&currencyCode=EUR`
      ).pipe(
        map((acceptedCash: any) => {
          return acceptedCash && (
            (acceptedCash.coins && acceptedCash.coins.length)
            || (acceptedCash.banknotes && acceptedCash.banknotes.length));
        }),
        catchError((error) => {
          return of(false);
        })
      );
  }

  canPayoutAmount(amount: number): Observable<boolean> {
    return this.http
      .get(
        `${this.vuBaseUrl}/api/payment/can-payout-amount/?value=${amount}&currencyCode=EUR`
      ).pipe(
        mapTo(true),
        catchError((error) => {
          return of(false);
        })
      );
  }

  howMuchCanPayoutAmount(amount: number): Observable<number> {
    return this.http
      .get(
        `${this.vuBaseUrl}/api/payment/how-much-can-payout-amount/?value=${amount}&currencyCode=EUR`
      ).pipe(
        map((availableAmount: any) => {
          return availableAmount ? availableAmount.value : 0;
        })
      );
  }

  redirectToExternalRefund(orderId: number): void {
    this.stopShopServices().subscribe(_ => {
      let url = `${this.vuBaseUrl}/store/external-payment?`;
      url += `order_id=${orderId}`;
      url += `&url=${environment.odooPaymentControllerUrl}`;
      url += `&payment_type=${VuPaymentType.Cash}`;
      url += `&return_url=${this.location.prepareExternalUrl('/payment/vu-complete')}`;
      url += `&cancel_url=${this.location.prepareExternalUrl('/payment/cancel/')}${orderId}`;
      url += `&lang=${this.translateService.currentLang}`;
      window.location.href = url;
    });
  }

  redirectToExternalUnlimitedRechargeCard(productId: number, cardNumber: string, ean13: string,
    cardBalance: number, personalNumber: string): void {
    this.stopShopServices().subscribe(_ => {
      let url = `${this.vuBaseUrl}/store/external-payment?`;
      url += `&product_id=${productId}`;
      url += `&card_number=${cardNumber}`;
      url += `&ean13=${ean13}`;
      url += `&card_balance=${cardBalance}`;
      url += `&personal_number=${personalNumber}`;
      url += `&url=${environment.odooPaymentControllerUrl}`;
      url += `&payment_type=${VuPaymentType.Cash}`;
      url += `&return_url=${this.location.prepareExternalUrl('/payment/vu-complete')}`;
      url += `&cancel_url=${this.location.prepareExternalUrl('/payment/cancel/')}`;
      url += `&lang=${this.translateService.currentLang}`;
      window.location.href = url;
    });
  }

  stopShopServices(): Observable<any> {
    return this.touchKeyboardHide();
  }

  switchLntBoard(enabled: boolean): Promise<any> {
    return this.http
      .post(`${this.vuBaseUrl}/api/touch-tile/switch-lnt-board`, { enabled: enabled })
      .toPromise()
      .catch(error => {
        this.loggerService.error(error);
        return Promise.reject(error.message || error);
      });
  }
}
